import { Loader, MultiSelect } from '@mantine/core';
import { Coding } from '@medplum/fhirtypes';
import { IconSearch } from '@tabler/icons-react';
import { FC, useEffect, useState } from 'react';

interface SearchResult {
  id: string;
  name: string;
  definition?: string;
  discription?: string;
}

interface AutoCompleteInputProps {
  apiurl?: string;
  system?: string;
  prefix?: string;
  isMulti?: boolean;
  value?: Coding | Coding[];
  onChange: (coding?: Coding | Coding[]) => void;
}

export const AutoCompleteInput: FC<AutoCompleteInputProps> = ({
  apiurl,
  system,
  prefix,
  isMulti,
  value,
  onChange,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [selectedCodings, setSelectedCodings] = useState<Coding[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (value) {
      if (Array.isArray(value)) {
        setSelectedCodings(value);
      } else {
        setSelectedCodings([value]);
      }
    } else {
      setSelectedCodings([]);
    }
  }, [value]);

  // ---------------------------------------------------------------------------
  const searchApi = async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${apiurl}${encodeURIComponent(query)}`);
      const data = await response.json();

      // Filter only ids that start with prefix
      let results;
      if (prefix) {
        results = data?.results?.filter((entry: any) =>
          entry.id?.startsWith(prefix)
        );
      } else {
        //TODO: make this generic
        results = data?.terms;
      }

      if (results.length === 0) {
        setSearchResults([]);
        return;
      }

      const formattedResults = results.map((item: any) => ({
        id: item.id || item.curie,
        name: item.label || item.name,
        description: item.description,
        definition: item.definition,
      }));

      setSearchResults(formattedResults);
    } catch (error) {
      console.error('Error fetching Omim data:', error);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  //----------------------------------------------------------------------------
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    searchApi(value);
  };

  // ---------------------------------------------------------------------------
  const handleValueChange = (selectedNames: string[]) => {
    if (selectedNames.length === 0) {
      setSelectedCodings([]);
      onChange(undefined);
      return;
    }

    // Find newly selected items by comparing selectedNames with selectedCodings
    const newlySelected = searchResults.filter(
      (r) =>
        selectedNames.includes(r.name) &&
        !selectedCodings.some((c) => c.code === r.id)
    );

    // Add newly selected to existing ones, filter out unselected
    const updatedSelected = [
      ...selectedCodings.filter((c) =>
        selectedNames.includes(
          // Map c.code back to name from searchResults or fallback to display
          searchResults.find((r) => r.id === c.code)?.name ?? c.display ?? ''
        )
      ),
      ...newlySelected.map((item) => ({
        system,
        code: item.id,
        display: item.name,
      })),
    ];

    setSelectedCodings(updatedSelected);
    onChange(
      isMulti ? updatedSelected : updatedSelected[updatedSelected.length - 1]
    );
  };

  //----------------------------------------------------------------------------

  return (
    <MultiSelect
      data={searchResults.map((result) => result.name)}
      value={
        isMulti
          ? selectedCodings.map((c) => {
              const found = searchResults.find((r) => r.id === c.code);
              return found ? found.name : (c.display ?? '');
            })
          : selectedCodings.length > 0
            ? [selectedCodings[0].display ?? '']
            : []
      }
      onChange={handleValueChange}
      onSearchChange={handleSearchChange}
      searchValue={searchQuery}
      maxValues={isMulti ? 10 : 1}
      searchable={isMulti ? true : selectedCodings.length === 0}
      onBlur={() => setSearchQuery('')}
      rightSection={isLoading ? <Loader size="xs" /> : <IconSearch size={18} />}
      size="md"
      radius="sm"
      mb="md"
    />
  );
};
